import React from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { ErrorState } from './ErrorState';
import { StaleDataNotice } from './StaleDataNotice';

/** Spacing and wrapping of a cell, kept apart from its typography. */
const CELL_LAYOUT = 'py-4 px-4 whitespace-nowrap';
const CELL_BASE = 'text-sm text-ninja-dark dark:text-white';
const HEADER_BASE = 'text-[11px] font-semibold text-text-muted uppercase tracking-wider';

/**
 * @param {object} props
 * @param {string|null} [props.error] - Why the load failed. Without it the table
 *   would say "No se encontraron resultados", which is what it used to do for a
 *   dead backend, an expired session and a filter that matched nothing alike.
 * @param {() => void} [props.onRetry]
 * @param {string} [props.emptyMessage] - Shown when there is genuinely nothing.
 *   Two pages have been passing this for a while and the component ignored it.
 * @param {(row: object) => boolean} [props.isRowSelectable] - Without it, every row.
 *   The others show their box off, and selecting the page leaves them out.
 * @param {(row: object) => string|undefined} [props.selectionHint] - Why a row cannot be selected.
 */
export const DataTable = ({
  columns,
  data = [],
  totalItems = 0,
  currentPage = 1,
  itemsPerPage = 50,
  isLoading = false,
  onPageChange,
  onRowsPerPageChange,
  selectedRows = [],
  onSelectionChange,
  isRowSelectable,
  selectionHint,
  idAccessor = 'id',
  itemLabel = 'ordenes',
  error = null,
  onRetry,
  emptyMessage = 'No se encontraron resultados'
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;

  const getRowId = (row, index) => {
    if (!row) return index;
    if (row[idAccessor] !== undefined && row[idAccessor] !== null) return row[idAccessor];
    if (row.id !== undefined && row.id !== null) return row.id;
    if (row.delivery_id !== undefined && row.delivery_id !== null) return row.delivery_id;
    return index;
  };

  const canSelect = (row) => !isRowSelectable || isRowSelectable(row);
  const selectableIds = data.flatMap((row, i) => (canSelect(row) ? [getRowId(row, i)] : []));

  const handleSelectAll = (e) => {
    if (!onSelectionChange) return;
    try {
      const isChecked = e.target.checked;
      const safeSelectedRows = Array.isArray(selectedRows) ? selectedRows : [];
      const allIds = selectableIds;

      if (isChecked) {
        const newSelected = Array.from(new Set([...safeSelectedRows, ...allIds]));
        onSelectionChange(newSelected);
      } else {
        const newSelected = safeSelectedRows.filter(id => !allIds.includes(id));
        onSelectionChange(newSelected);
      }
    } catch (err) {
      console.error("Error in handleSelectAll", err);
    }
  };

  const handleSelectRow = (id) => {
    if (!onSelectionChange) return;
    try {
      const safeSelectedRows = Array.isArray(selectedRows) ? selectedRows : [];
      if (safeSelectedRows.includes(id)) {
        onSelectionChange(safeSelectedRows.filter(r => r !== id));
      } else {
        onSelectionChange([...safeSelectedRows, id]);
      }
    } catch (err) {
      console.error("Error in handleSelectRow", err);
    }
  };

  const isAllSelected = selectableIds.length > 0 && Array.isArray(selectedRows) && selectableIds.every((id) => {
    return selectedRows.includes(id);
  });

  const renderPaginationButtons = () => {
    const buttons = [];
    let startPage = Math.max(1, currentPage - 1);
    let endPage = Math.min(totalPages, startPage + 2);

    if (endPage - startPage < 2) {
      startPage = Math.max(1, endPage - 2);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => onPageChange?.(i)}
          className={clsx(
            "w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium transition-all",
            currentPage === i
              ? "bg-ninja-green text-white border-transparent"
              : "border border-transparent text-text-secondary hover:bg-ninja-gray dark:hover:bg-[#2d2d2d]"
          )}
        >
          {i}
        </button>
      );
    }
    return buttons;
  };

  return (
    <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border overflow-hidden">
      {/* Rows from before stay under it; without rows the error goes in the body. */}
      {error && data.length > 0 && (
        <StaleDataNotice
          testId="data-table-stale-error"
          className="p-3 border-b border-border"
          error={error}
          onRetry={onRetry}
        />
      )}
      <div className="overflow-x-auto relative">
        {isLoading && (
          <div className="absolute inset-0 bg-white/50 dark:bg-black/50 z-10 flex items-center justify-center backdrop-blur-sm">
            <Loader2 className="w-8 h-8 text-ninja-green animate-spin" />
          </div>
        )}
        <table data-testid="data-table" className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-ninja-gray dark:bg-[#0f0f0f] border-b border-border">
              {onSelectionChange && (
                <th className="p-4 w-12 text-center">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    disabled={selectableIds.length === 0}
                    aria-label="Seleccionar todas las filas"
                    data-testid="data-table-select-all"
                    className="w-4 h-4 rounded border-border text-ninja-green focus:ring-ninja-green bg-white dark:bg-[#1a1a1a] cursor-pointer"
                  />
                </th>
              )}
              {columns.map((col, i) => (
                <th key={i} className={clsx(HEADER_BASE, CELL_LAYOUT)}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.length === 0 && !isLoading ? (
              <tr>
                <td colSpan={columns.length + (onSelectionChange ? 1 : 0)} className="py-8 text-center text-text-secondary">
                  {error ? (
                    <ErrorState
                      variant="compact"
                      title="No se pudieron cargar los datos"
                      description={error}
                      onRetry={onRetry}
                    />
                  ) : (
                    emptyMessage
                  )}
                </td>
              </tr>
            ) : (
              data.map((row, i) => {
                const rowId = getRowId(row, i);
                const isSelected = Array.isArray(selectedRows) && selectedRows.includes(rowId);
                const selectable = canSelect(row);
                return (
                  <tr
                    key={rowId}

                    data-testid="data-table-row"
                    data-row-id={rowId}
                    className={clsx("hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] transition-colors group", isSelected && "bg-ninja-green/5 dark:bg-ninja-green/10")}
                  >
                    {onSelectionChange && (
                      <td className="p-4 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectRow(rowId)}
                          disabled={!selectable}
                          title={selectable ? undefined : selectionHint?.(row)}
                          aria-label={`Seleccionar la fila ${rowId}`}
                          data-testid="data-table-select-row"
                          className={clsx(
                            "w-4 h-4 rounded border-border text-ninja-green focus:ring-ninja-green bg-white dark:bg-[#1a1a1a]",
                            selectable ? "cursor-pointer" : "cursor-not-allowed opacity-40"
                          )}
                        />
                      </td>
                    )}
                    {columns.map((col, j) => (
                      <td key={j} className={clsx(CELL_BASE, CELL_LAYOUT)}>
                        {col.cell ? col.cell(row) : row[col.accessor]}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 sm:px-6 py-4 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div data-testid="data-table-summary" className="text-sm text-text-secondary">
          Mostrando {totalItems > 0 ? startIndex + 1 : 0}-{Math.min(startIndex + itemsPerPage, totalItems)} de {totalItems} {itemLabel}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => onPageChange?.(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1 || isLoading}
            aria-label="Página anterior"
            data-testid="data-table-prev-page"
            className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-text-secondary hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={16} />
          </button>

          <div className="flex items-center gap-1">
            {currentPage > 3 && (
              <>
                <button
                  onClick={() => onPageChange?.(1)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium border border-transparent text-text-secondary hover:bg-ninja-gray dark:hover:bg-[#2d2d2d]"
                >
                  1
                </button>
                <span className="text-text-secondary px-1">...</span>
              </>
            )}

            {renderPaginationButtons()}

            {currentPage < totalPages - 2 && (
              <>
                <span className="text-text-secondary px-1">...</span>
                <button
                  onClick={() => onPageChange?.(totalPages)}
                  className="w-auto px-2 h-8 rounded-lg flex items-center justify-center text-sm font-medium border border-transparent text-text-secondary hover:bg-ninja-gray dark:hover:bg-[#2d2d2d]"
                >
                  {totalPages}
                </button>
              </>
            )}
          </div>

          <button
            onClick={() => onPageChange?.(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages || totalPages === 0 || isLoading}
            aria-label="Página siguiente"
            data-testid="data-table-next-page"
            className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-text-secondary hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight size={16} />
          </button>

          <div className="sm:ml-4 flex items-center gap-2 text-sm text-text-secondary">
            Filas:
            <select
              value={itemsPerPage}
              onChange={(e) => onRowsPerPageChange?.(Number(e.target.value))}
              disabled={isLoading}
              aria-label="Filas por página"
              data-testid="data-table-rows-per-page"
              className="border border-border rounded-lg px-2 py-1 bg-white dark:bg-[#1a1a1a] text-ninja-dark dark:text-white outline-none cursor-pointer disabled:opacity-50"
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

